import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { get, post } from "./client";
import type { ApiResponse } from "./deals";
import { isApiError } from "./errors";
import { queryKeys } from "./queryKeys";

export interface CriticalGapActionDto {
  id: number;
  priority: string;
  actionText: string;
  ownerHint?: string | null;
  timeframe?: string | null;
  escalationRequired: boolean;
  status: string;
  sortOrder: number;
  notes?: string | null;
}

export interface CriticalGapEvidenceLinkDto {
  id: number;
  /** Snapshot row the cited activity came from, or 0 when it had none. */
  planningSnapshotItemId: number;
  /** Canonical id of the cited activity. Carries the identity when the snapshot id is 0. */
  stableItemId?: string | null;
  evidenceRole?: string | null;
  matchScore?: number | null;
  excerpt?: string | null;
}

export interface CriticalGapRequirementResultDto {
  id: number;
  requirementId: string;
  requirementTitle: string;
  requirementDescription?: string | null;
  coverageStatus: string;
  impactLevel: string;
  coverageConfidence?: number | null;
  matchScore?: number | null;
  matchedActivityCount: number;
  isCriticalGap: boolean;
  /** Identifier of the planning activity the backend picked as the primary evidence for this requirement. */
  primaryEvidenceActivityId?: string | null;
  /** Backend's qualitative confidence in the best requirement→activity mapping (e.g. "high"). */
  bestMappingConfidence?: string | null;
  /** Backend's qualitative assessment of the supporting evidence (e.g. "strong"). */
  evidenceQuality?: string | null;
  lifecycleStatus: string;
  analysisMethod: string;
  analysisTimestamp: string;
  rationale?: string | null;
  missingEvidence?: string | null;
  /**
   * What goes wrong on Day 1 if the requirement stays open. Authored against the
   * requirement itself rather than derived from coverage, so it is the only field
   * that states the consequence of the gap rather than the shape of the evidence.
   */
  day1Risk?: string | null;
  /**
   * Which checklist or research pass the requirement originated from
   * (`general` / `sector` / `acquisition` / `web_research`).
   *
   * Distinct from `analysisMethod`, which on a stage 2 row describes how the requirement was
   * *matched* against the deal plan, not where it came from. Only this field can tell you that
   * the web research proposed a requirement.
   */
  requirementSource?: string | null;
  /** Composite 0–1 priority. Computed in code from the dimensions; never asked of the model directly. */
  priorityScore?: number | null;
  /** `llm_priority_v1` or `heuristic_priority_v1` — how the score above was arrived at. */
  priorityMethod?: string | null;
  priorityRationale?: string | null;
  /** Dimension scores, 0–5, higher is worse. Null means unscored, which is not the same as 0. */
  priorityCompletionBlocker?: number | null;
  priorityTransferOfControlBlocker?: number | null;
  priorityLicenceRegulatoryBreach?: number | null;
  priorityFinancialExposure?: number | null;
  priorityReversibility?: number | null;
  /** The plan activity the match judge chose, for audit. Null when it chose none. */
  matchedActivity?: CriticalGapMatchedActivityDto | null;
  /** Every activity accepted as evidence in the requirement's own workstream. */
  matchedActivities?: CriticalGapMatchedActivityDto[] | null;
  /** Equivalent or related activities found in other workstreams; never coverage on their own. */
  relatedActivitiesElsewhere?: CriticalGapMatchedActivityDto[] | null;
  /** Canonical coverage fields, copied unchanged from the authoritative coverage step. */
  gapType?: string | null;
  gapPresence?: string | null;
  recommendedAction?: string | null;
  coverageStatusLabel?: string | null;
  coverageDecisionSource?: string | null;
  actions: CriticalGapActionDto[];
  evidenceLinks: CriticalGapEvidenceLinkDto[];
}

/**
 * The deal-plan activity a requirement was judged against.
 *
 * Every field is optional because deal plans routinely omit owner and timing; a sparse match is
 * still a real match. `timingSource` records whether `timing` came from the activity's own phase
 * label or was inferred from its dates — they do not carry the same weight.
 */
export interface CriticalGapMatchedActivityDto {
  activityId?: string | null;
  taskName?: string | null;
  workstream?: string | null;
  owner?: string | null;
  timing?: string | null;
  timingSource?: string | null;
  activityType?: string | null;
  activityCategory?: string | null;
  mappingConfidence?: string | null;
  mappingConfidenceReason?: string | null;
}

export interface CriticalGapWorkstreamResultDto {
  id: number;
  workstreamId?: number | null;
  workstreamName: string;
  coveragePercent?: number | null;
  confidence?: number | null;
  summary?: string | null;
  totalRequirements: number;
  coveredCount: number;
  partiallyCoveredCount: number;
  notCoveredCount: number;
  highImpactCount: number;
  mediumImpactCount: number;
  lowImpactCount: number;
  requirements: CriticalGapRequirementResultDto[];
}

/**
 * A gap named in an executive summary's top list.
 *
 * These are a *selection*, not a recomputation: the programme pass picks from the work streams'
 * own scored gaps rather than re-scoring them, so the values here match the register rows they
 * came from.
 */
export interface CriticalGapExecutiveSummaryGapDto {
  id: number;
  sortOrder: number;
  requirementId: string;
  requirementTitle: string;
  workstreamName?: string | null;
  coverageStatus: string;
  priorityScore?: number | null;
  day1Risk?: string | null;
  recommendedAction?: string | null;
}

/**
 * A narrative summary over one work stream, or over the whole programme.
 *
 * `scope` is `"workstream"` or `"programme"`. There is at most one programme row per run, and it
 * reads as the conclusion — the backend orders work streams first for that reason.
 */
export interface CriticalGapExecutiveSummaryDto {
  id: number;
  scope: string;
  workstreamId?: number | null;
  workstreamName?: string | null;
  workstreamCode?: string | null;
  coveredCount: number;
  partiallyCoveredCount: number;
  notCoveredCount: number;
  gapCount: number;
  summary?: string | null;
  topGaps: CriticalGapExecutiveSummaryGapDto[];
}

export const CRITICAL_GAP_SUMMARY_SCOPE_WORKSTREAM = "workstream";
export const CRITICAL_GAP_SUMMARY_SCOPE_PROGRAMME = "programme";

export interface CriticalGapAnalysisRunDto {
  id: number;
  dealId: number;
  checklistVersion: string;
  contextTimestamp: string;
  contextFingerprint?: string | null;
  status: string;
  startedAt: string;
  completedAt?: string | null;
  failedAt?: string | null;
  failureReason?: string | null;
  totalWorkstreams: number;
  totalRequirements: number;
  coveredCount: number;
  partiallyCoveredCount: number;
  notCoveredCount: number;
  highImpactCount: number;
  mediumImpactCount: number;
  lowImpactCount: number;
  actionCount: number;
  overallConfidence?: number | null;
  summary?: string | null;
  createdDate: string;
  modifiedDate?: string | null;
  workstreams: CriticalGapWorkstreamResultDto[];

  /**
   * One entry per work stream plus a programme row, work streams first.
   *
   * Populated only on detail reads. Unlike `workstreams`, this survives the approval gate: a
   * summary carries no per-requirement approval state, so there is nothing in it to leak.
   */
  executiveSummaries?: CriticalGapExecutiveSummaryDto[];

  /**
   * Conditions raised while assembling the checklist these results were measured against. They
   * belong to the stage 1 run that built it, not to this run, which did no research.
   */
  checklistWarnings?: string[];
}

export interface UseCriticalGapQueryOptions {
  enabled?: boolean;
  refetchIntervalMs?: number;
  /**
   * Keep polling even when nothing is active, for callers that are waiting for analysis to
   * start rather than to finish.
   *
   * Pass a predicate when the answer depends on the current time: it is evaluated each time the
   * next interval is computed, so a time-boxed window closes on its own without needing a render
   * to notice.
   */
  pollWhenIdle?: boolean | (() => boolean);
}

export interface CriticalGapRunStatusDetailDto {
  phase?: string | null;
  stage?: string | null;
  stageIndex?: number | null;
  stageCount?: number | null;
  progressPercent?: number | null;
  message?: string | null;
  updatedAt?: string | null;
}

export interface CriticalGapAnalysisJobDto {
  id: number;
  dealId?: number | null;
  documentId?: number | null;
  planningSnapshotId?: number | null;
  processingJobId?: number | null;
  runId?: number | null;
  triggerSource: string;
  stage?: string | null;
  status: string;
  requestedBy: string;
  requestCorrelationKey: string;
  workflowJobId?: string | null;
  error?: string | null;
  dispatchedAt?: string | null;
  completedAt?: string | null;
  createdDate: string;
  modifiedDate?: string | null;
  runStatus?: string | null;
  runStatusDetail?: CriticalGapRunStatusDetailDto | null;
}

export interface CriticalGapDay1ReadinessContextDto {
  pageData?: {
    workstreams?: CriticalGapWorkstreamContextDto[] | null;
  } | null;
}

export interface CriticalGapWorkstreamContextDto {
  workstreamId?: number | null;
  workstreamCode?: string | null;
  workstreamName?: string | null;
  normalizedWorkstreamKey?: string | null;
  last_updated_at?: string | null;
  lastUpdatedAt?: string | null;
}

const ACTIVE_JOB_STATUSES = new Set(["queued", "in_progress"]);
const ACTIVE_RUN_STATUSES = new Set(["pending", "queued", "in_progress"]);
const COMPLETED_RUN_STATUSES = new Set(["completed"]);
const FAILED_RUN_STATUSES = new Set(["failed"]);

function normalizeStatus(status: string | null | undefined): string {
  return status?.trim().toLowerCase() ?? "";
}

export function isCriticalGapAnalysisJobActive(
  job: Pick<CriticalGapAnalysisJobDto, "status"> | null | undefined
): boolean {
  return ACTIVE_JOB_STATUSES.has(normalizeStatus(job?.status));
}

export function isCriticalGapAnalysisRunInProgress(
  run: Pick<CriticalGapAnalysisRunDto, "status" | "completedAt" | "failedAt"> | null | undefined
): boolean {
  if (run == null) {
    return false;
  }

  if (run.completedAt != null || run.failedAt != null) {
    return false;
  }

  return ACTIVE_RUN_STATUSES.has(normalizeStatus(run.status));
}

export function isCriticalGapAnalysisRunCompleted(
  run: Pick<CriticalGapAnalysisRunDto, "status" | "completedAt"> | null | undefined
): boolean {
  if (run == null) {
    return false;
  }

  if (run.completedAt != null) {
    return true;
  }

  return COMPLETED_RUN_STATUSES.has(normalizeStatus(run.status));
}

export function isCriticalGapAnalysisRunFailed(
  run: Pick<CriticalGapAnalysisRunDto, "status" | "failedAt"> | null | undefined
): boolean {
  if (run == null) {
    return false;
  }

  if (run.failedAt != null) {
    return true;
  }

  return FAILED_RUN_STATUSES.has(normalizeStatus(run.status));
}

export function getLatestCriticalGapAnalysisRun() {
  return get<ApiResponse<CriticalGapAnalysisRunDto | null>>("/critical-gap-analysis/runs/latest");
}

export function getCriticalGapDay1ReadinessContext() {
  return get<ApiResponse<CriticalGapDay1ReadinessContextDto>>(
    "/critical-gap-analysis/context/day1-readiness"
  );
}

export function useCriticalGapDay1ReadinessContext(enabled = true) {
  return useQuery({
    queryKey: queryKeys.api.criticalGapDay1ReadinessContext(),
    queryFn: async () => {
      const response = await getCriticalGapDay1ReadinessContext();
      return response.data ?? null;
    },
    enabled,
  });
}

export function triggerManualGapAnalysis() {
  return post<ApiResponse<{ jobId: string | null; reason: string | null }>>("/critical-gap-analysis/trigger");
}

export function retryFailedCriticalGapAnalysis() {
  return post<ApiResponse<string>>("/critical-gap-analysis/retry");
}

export function useRetryFailedCriticalGapAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryFailedCriticalGapAnalysis,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.api.criticalGapJobs() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.api.criticalGapLatestRun() });
    },
  });
}

function resolvePollWhenIdle(pollWhenIdle: boolean | (() => boolean)): boolean {
  return typeof pollWhenIdle === "function" ? pollWhenIdle() : pollWhenIdle;
}

export function useLatestCriticalGapAnalysisRun(
  { enabled = true, refetchIntervalMs, pollWhenIdle = false }: UseCriticalGapQueryOptions = {}
) {
  return useQuery({
    queryKey: queryKeys.api.criticalGapLatestRun(),
    queryFn: async () => {
      try {
        const response = await getLatestCriticalGapAnalysisRun();
        return response.data ?? null;
      } catch (error) {
        if (isApiError(error) && error.status === 404) {
          return null;
        }

        throw error;
      }
    },
    enabled,
    refetchInterval: refetchIntervalMs
      ? (query) => {
          const run = query.state.data;
          if (!run) return refetchIntervalMs;
          // Stop polling once the run has reached a terminal state
          const isTerminal = isCriticalGapAnalysisRunCompleted(run) || isCriticalGapAnalysisRunFailed(run);
          // ...unless the caller is waiting for a *new* run to start. The latest run being
          // terminal is the normal state while a fresh one is being queued, so treating it as
          // "nothing more to see" would stop polling exactly when it is needed.
          if (!isTerminal) return refetchIntervalMs;
          return resolvePollWhenIdle(pollWhenIdle) ? refetchIntervalMs : false;
        }
      : undefined,
  });
}

export function useCriticalGapAnalysisJobs(
  refetchIntervalMs = 4_000,
  { pollWhenIdle = false }: Pick<UseCriticalGapQueryOptions, "pollWhenIdle"> = {}
) {
  return useQuery({
    queryKey: queryKeys.api.criticalGapJobs(),
    queryFn: async () => {
      const response = await get<ApiResponse<CriticalGapAnalysisJobDto[]>>(
        "/critical-gap-analysis/jobs?take=10"
      );
      return response.data ?? [];
    },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (!jobs) return refetchIntervalMs;
      const hasActive = jobs.some((job) => isCriticalGapAnalysisJobActive(job));
      if (hasActive) return refetchIntervalMs;
      // No active job is also what "the job we are waiting for has not been created yet"
      // looks like, so callers expecting one keep polling rather than stopping on the gap
      // between the trigger and the queue.
      return resolvePollWhenIdle(pollWhenIdle) ? refetchIntervalMs : false;
    },
    refetchOnMount: "always",
  });
}
